import { FC } from 'react';
import type { TableData } from '@libs/types';
import { Box, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Markdown } from '@components/Markdown';

/**
 * A `table` block, rendered read-only.
 *
 * Unlike every other block kind, a table never carries a change — the AI's
 * contract restricts edits to heading/paragraph/list_item, so there is no
 * highlight, pin or strikethrough state to plumb through here. This is pure
 * pass-through of `headerRows`/`rows`, the same split the delivered PDF/.docx
 * renders from.
 *
 * `headerRows` can legitimately be empty (a label/value table with no header
 * band), so `<thead>` is only emitted when there is one to show. Cells go
 * through `Markdown` in inline mode because they carry the same restricted
 * `**bold**`/`*italic*` subset as `block.text`.
 */
export const RemediationTable: FC<{ table: TableData | null | undefined }> = observer(
  ({ table }) => {
    const theme = useTheme();
    if (!table || (table.headerRows.length === 0 && table.rows.length === 0)) return null;

    const cellSx = {
      border: `1px solid ${theme.palette.grey[300]}`,
      // Logical, not `left`/`right` — this app renders Arabic and a table
      // flips direction with the rest of the document.
      paddingInline: '0.6rem',
      paddingBlock: '0.35rem',
      textAlign: 'start' as const,
      verticalAlign: 'top' as const,
    };

    return (
      // The scroll lives on this wrapper, never on the table itself, so a
      // 5-column table scrolls inside its own box instead of forcing the
      // narrow review pane it sits in to scroll sideways.
      <Box sx={{ overflowX: 'auto', my: 1 }}>
        <Box
          component="table"
          sx={{
            borderCollapse: 'collapse',
            width: 'max-content',
            minWidth: '100%',
            // Inherit the surrounding document type rather than setting its
            // own — a table is part of the document body, not a UI widget
            // dropped into it.
            font: 'inherit',
          }}
        >
          {table.headerRows.length > 0 && (
            <Box component="thead">
              {table.headerRows.map((row, ri) => (
                <Box component="tr" key={ri}>
                  {row.map((cell, ci) => (
                    <Box
                      component="th"
                      key={ci}
                      sx={{ ...cellSx, backgroundColor: theme.palette.grey[100], fontWeight: 700 }}
                    >
                      <Markdown inline>{cell}</Markdown>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          )}
          <Box component="tbody">
            {table.rows.map((row, ri) => (
              <Box component="tr" key={ri}>
                {row.map((cell, ci) => (
                  <Box component="td" key={ci} sx={cellSx}>
                    <Markdown inline>{cell}</Markdown>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  },
);
